import React, { useCallback } from 'react';
import styled from 'styled-components';
import { Typography } from 'src/typography';
import { useRouter } from 'next/compat/router';
import { DesktopOnly } from 'shared/components';
import useDispensary from 'src/dispensary/hooks/use-dispensary';
import { useInstoreOrderReceiptModal } from 'src/components/modals/instore-order-receipt/instore-order-receipt-controller';
import { ConsumerUser, ConsumerOrder } from '../../account.types';
import { OrderListTabBar, OrderSource, TAB_IDS, TAB_PANEL_IDS, OrderTab } from './order-list-tab-bar';
import { OrderList } from './order-list';
import { useOrdersForConsumer, queryMoreOrders } from './use-orders-for-consumer';
import { useOrderList } from './use-order-list';
import { useInstoreOrderList } from './use-instore-orders-for-consumer/use-instore-order-list';

type TabbedOrderListControllerProps = {
  user: ConsumerUser;
};

const EMPTY_SET = new Set<string>();

export function TabbedOrderListController({ user }: TabbedOrderListControllerProps): JSX.Element {
  const router = useRouter();
  const { dispensary } = useDispensary();

  const rawTab = router?.query.tab;
  const activeTab = rawTab === OrderTab.instore ? OrderTab.instore : OrderTab.online;
  const isOnline = activeTab === OrderTab.online;

  const handleTabChange = useCallback(
    (tab: OrderTab): void => {
      void router?.push({
        pathname: router.pathname,
        query: { ...router.query, tab },
      });
    },
    [router]
  );

  // Online orders
  const {
    orders,
    loading: onlineLoading,
    fetchMore,
    totalCount,
    hasError: onlineHasError,
    refetch: onlineRefetch,
  } = useOrdersForConsumer({
    dispensary,
    skip: !isOnline,
  });

  const handleLoadMoreOnline = useCallback((): void => {
    queryMoreOrders(fetchMore, orders);
  }, [fetchMore, orders]);

  const { viewableStatuses, showLoadingSpinner, handleViewOrder } = useOrderList({
    loading: onlineLoading,
    orders,
    user,
  });

  // In-store orders
  const {
    orders: instoreOrders,
    rawOrders,
    loading: instoreLoading,
    hasMore: instoreHasMore,
    hasError: instoreHasError,
    handleLoadMore: handleLoadMoreInstore,
    refetch: instoreRefetch,
  } = useInstoreOrderList({
    dispensaryId: dispensary?.id ?? '',
    skip: isOnline || !dispensary?.id,
  });

  const { open: openInstoreReceiptModal } = useInstoreOrderReceiptModal();

  const handleViewInstoreOrder = useCallback(
    (order: ConsumerOrder): void => {
      if (order.orderId) {
        const posOrder = rawOrders.find((o) => o.id === order.orderId);
        if (posOrder) {
          void openInstoreReceiptModal({ posOrder });
        }
      }
    },
    [rawOrders, openInstoreReceiptModal]
  );

  return (
    <>
      <DesktopOnly>
        <OrdersHeading tag='h2' size='medium'>
          Orders
        </OrdersHeading>
      </DesktopOnly>
      <OrderListTabBar selectedTab={activeTab} onTabChange={handleTabChange} />

      {isOnline ? (
        <div id={TAB_PANEL_IDS.online} role='tabpanel' aria-labelledby={TAB_IDS.online}>
          <OrderList
            handleLoadMore={handleLoadMoreOnline}
            hasMoreOrders={orders.length < totalCount}
            loading={onlineLoading}
            orders={orders}
            viewableStatuses={viewableStatuses}
            showLoadingSpinner={showLoadingSpinner}
            handleViewOrder={handleViewOrder}
            hideHeader
            orderSource={OrderSource.online}
            hasError={onlineHasError}
            onRefetch={onlineRefetch}
          />
        </div>
      ) : (
        <div id={TAB_PANEL_IDS.instore} role='tabpanel' aria-labelledby={TAB_IDS.instore}>
          <OrderList
            handleLoadMore={handleLoadMoreInstore}
            hasMoreOrders={instoreHasMore}
            loading={instoreLoading}
            orders={instoreOrders}
            viewableStatuses={EMPTY_SET}
            showLoadingSpinner={instoreLoading}
            handleViewOrder={handleViewInstoreOrder}
            hideHeader
            orderSource={OrderSource.inStore}
            hasError={instoreHasError}
            onRefetch={instoreRefetch}
          />
        </div>
      )}
    </>
  );
}

export const OrdersHeading = styled(Typography.Heading)`
  margin-bottom: 12px;
  color: var(--dcm-color-text-primary);
`;
